/* eslint-disable @typescript-eslint/prefer-as-const */
import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, makeStyles } from '@material-ui/core';
import { SnackbarProvider } from 'notistack';

import { Notification } from '../../types/notifications';

import { useDesktopMode } from '../../helpers/hooks';
import { uuid } from '../../helpers/string';

const useStyle = makeStyles((theme) => ({
  success: { backgroundColor: theme.palette.success.main },
  error: { backgroundColor: theme.palette.error.main },
  warning: { backgroundColor: theme.palette.warning.main },
  info: { backgroundColor: theme.palette.info.main },
  actionClose: {
    height: theme.spacing(4),
    minWidth: theme.spacing(4),
    fontSize: '1.3em',
    width: 'fit-content',
    position: 'absolute',
    top: theme.spacing(1),
    right: 0,
  },
}));

const defaultMaxToastDisplayed = 5;
const defaultToastDismissTime = 3000;

const toastPositionDesktop = {
  vertical: 'bottom' as 'bottom',
  horizontal: 'right' as 'right',
};

const toastPositionMobile = {
  vertical: 'bottom' as 'bottom',
  horizontal: 'right' as 'right',
};

interface ToastNotificationProps {
  toasts?: Notification[];
  removeToast?: (toast: Notification) => void;
  maxToastDisplayed?: number;
  toastAutoDismiss?: number;
  origin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
}

export interface ToastContextType {
  enqueueToast: (toast: Notification) => void;
  closeToast: (key: React.ReactText) => void;
}

interface ToastActionProps {
  action: (key: React.ReactText) => void;
  context: React.ReactNode;
  identifier: React.ReactText;
}

export const ToastAction: React.FC<ToastActionProps> = ({ action, context, identifier }) => {
  const classes = useStyle();
  return (
    <IconButton onClick={() => action(identifier)} className={classes.actionClose}>
      {context}
    </IconButton>
  );
};

export const prepareToast = (toast: Notification, closeToast: ToastContextType['closeToast']) => {
  const closeContext = <FontAwesomeIcon icon={['far', 'times']} />;
  const preparedToast = { ...toast };
  if (!preparedToast.identifier) {
    preparedToast.identifier = uuid();
  }
  if (typeof preparedToast.withClose === 'undefined') {
    preparedToast.withClose = true; // default value to true
  }
  if (preparedToast.withClose) {
    preparedToast.action = closeToast;
    preparedToast.actionContext = closeContext;
  }
  preparedToast.autoHide = toast.autoHide ?? true;
  return preparedToast;
};

export const ToastContext = React.createContext<ToastContextType>({} as ToastContextType);

const ToastNotificationContainer: React.FC<ToastNotificationProps> = (props) => {
  const desktopLayout = useDesktopMode();
  const classes = useStyle();

  const notistackRef = React.createRef<SnackbarProvider>();

  const closeToast = (index: string | number) => {
    if (notistackRef.current) {
      notistackRef.current.closeSnackbar(index);
    }
  };

  const toastContext = {
    closeToast,
    enqueueToast: (toast: Notification) => {
      if (notistackRef.current) {
        const preparedToast = prepareToast(toast, closeToast);
        notistackRef.current.enqueueSnackbar(preparedToast.text, {
          key: preparedToast.identifier || uuid(),
          variant: preparedToast.severity,
          persist: !preparedToast.autoHide,
          // eslint-disable-next-line react/display-name
          action: (key) => {
            if (preparedToast.action) {
              return (
                <ToastAction
                  identifier={key}
                  action={preparedToast.action}
                  context={preparedToast.actionContext}
                />
              );
            }
            return null;
          },
        });
      }
    },
  };

  const {
    toasts = [],
    removeToast = () => void 0,
    maxToastDisplayed = defaultMaxToastDisplayed,
    toastAutoDismiss = defaultToastDismissTime,
    origin = desktopLayout ? toastPositionDesktop : toastPositionMobile,
    children,
  } = props;

  React.useEffect(() => {
    return toasts.forEach((toast) => {
      toastContext.enqueueToast(toast);
    });
  }, [toasts]);

  return (
    <SnackbarProvider
      ref={notistackRef}
      maxSnack={maxToastDisplayed}
      resumeHideDuration={toastAutoDismiss}
      anchorOrigin={origin}
      classes={{
        variantSuccess: classes.success,
        variantError: classes.error,
        variantWarning: classes.warning,
        variantInfo: classes.info,
      }}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onClose={(e: React.SyntheticEvent<any> | null, reason: string, key?: React.ReactText) => {
        if (reason === 'clickaway') {
          return;
        }

        if (!!key) {
          const toastToRemove = toasts.find((t) => t.identifier === key);
          if (!!toastToRemove) {
            removeToast(toastToRemove);
          }
        }
      }}
    >
      <ToastContext.Provider value={toastContext}>{children}</ToastContext.Provider>
    </SnackbarProvider>
  );
};

export default ToastNotificationContainer;
